import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Map, 
  Pentagon, 
  Anchor, 
  AlertTriangle, 
  Bookmark, 
  Settings, 
  ChevronLeft, 
  ChevronRight,
  LogOut
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useApp } from '@/context/AppContext';

const PELINDO_LOGO = 'https://seatrack.farovonmb.co.id/public/image/logo_pelindo_makasar.png';

const navItems = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: Map
  },
  {
    path: '/geofence',
    label: 'Geofence',
    icon: Pentagon
  },
  {
    path: '/stasiun-pandu',
    label: 'Stasiun Pandu',
    icon: Anchor
  },
  {
    path: '/vessel-anomaly',
    label: 'Vessel Anomaly',
    icon: AlertTriangle
  },
  {
    path: '/bookmark',
    label: 'Bookmark',
    icon: Bookmark
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: Settings
  }
];

export const Sidebar = () => {
  const location = useLocation();
  const { sidebarCollapsed, setSidebarCollapsed, logout } = useApp();

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-card border-r border-border transition-all duration-300 flex flex-col",
        sidebarCollapsed ? "w-[70px]" : "w-[260px]"
      )}
    >
      {/* Logo Section */}
      <div className={cn(
        "flex items-center h-16 px-4 border-b border-border",
        sidebarCollapsed ? "justify-center" : "gap-3"
      )}>
        <img 
          src={PELINDO_LOGO} 
          alt="Pelindo Makassar" 
          className="w-10 h-10 object-contain"
        />
        {!sidebarCollapsed && (
          <div className="flex flex-col">
            <span className="font-display font-semibold text-sm text-foreground">Pelindo Makassar</span>
            <span className="text-xs text-muted-foreground">Seatrack Engine</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        <TooltipProvider delayDuration={0}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path === '/dashboard' && location.pathname === '/');
            
            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.path}
                    className={cn(
                      "sidebar-nav-item",
                      isActive && "active",
                      sidebarCollapsed && "justify-center px-2"
                    )}
                  >
                    <item.icon className="w-5 h-5 shrink-0" />
                    {!sidebarCollapsed && (
                      <span className="text-sm">{item.label}</span>
                    )}
                  </NavLink>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right" className="font-medium">
                    {item.label}
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </nav>

      {/* Logout Button */}
      <div className="px-3 py-4 border-t border-border">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10",
                  sidebarCollapsed ? "justify-center px-2" : "justify-start gap-3"
                )}
                onClick={logout}
              >
                <LogOut className="w-5 h-5 shrink-0" />
                {!sidebarCollapsed && <span className="text-sm">Logout</span>}
              </Button>
            </TooltipTrigger>
            {sidebarCollapsed && (
              <TooltipContent side="right" className="font-medium">
                Logout
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Collapse Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-3 top-20 z-50 h-6 w-6 rounded-full border border-border bg-card shadow-sm hover:bg-accent"
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
      >
        {sidebarCollapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
      </Button>
    </aside>
  );
};

export default Sidebar;
